import { notifications } from "@mantine/notifications";
import { Loader } from "@mantine/core";
import dayjs from "dayjs";
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { X } from "lucide-react";
import {
  useCreateEventMutation,
  useDeleteEventImageMutation,
  useUpdateEventMutation,
  useUploadEventImageMutation,
} from "Services/EventApiSlice";
import { useGetAllEventRegistrationsQuery } from "Services/EventRegistrationApiSlice";
import { AdminEvent, AudienceType } from "Types/Admin/AdminEventType";
import { compressImage } from "Utilities";

type Props = {
  item?: AdminEvent | null;
  onSave: () => void;
  onCancel: () => void;
  mode?: "add" | "edit" | "view";
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e6e6e6",
  padding: 16,
  borderRadius: 6,
};

const fieldStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "8px 10px",
  borderRadius: 4,
  border: "1px solid #d9d9d9",
  boxSizing: "border-box",
};

const textareaStyle: React.CSSProperties = {
  ...fieldStyle,
  minHeight: 80,
  resize: "vertical",
};
const EVENT_DESCRIPTION_LIMIT = 1000;
const MAX_IMAGES = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

const EventsForm: React.FC<Props> = ({
  item = null,
  onSave,
  onCancel,
  mode = "add",
}) => {
  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();
  const [uploadEventImage] = useUploadEventImageMutation();

  const [startDate, setStartDate] = useState<Date | null>(() => {
    if (!item?.eventDate || !item?.eventStartTime) return null;

    const parsed = dayjs(
      `${item.eventDate} ${item.eventStartTime}`,
      "DD/MM/YYYY HH:mm",
      true,
    );

    return parsed.isValid() ? parsed.toDate() : null;
  });

  const [endDate, setEndDate] = useState<Date | null>(() => {
    const dateToUse = item?.eventEndDate || item?.eventDate;
    const timeToUse = item?.eventEndTime;

    if (!dateToUse || !timeToUse) return null;

    const parsed = dayjs(`${dateToUse} ${timeToUse}`, "DD/MM/YYYY HH:mm", true);

    return parsed.isValid() ? parsed.toDate() : null;
  });

  const RequiredLabel: React.FC<{ text: string }> = ({ text }) => (
    <label className="font-medium">
      {text} <span className="text-red-500">*</span>
    </label>
  );

  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [eventAddress, setEventAddress] = useState(item?.eventAddress ?? "");
  const [audience, setAudience] = useState<AudienceType[]>(
    item?.audience ?? [],
  );
  const [eventCity, setEventCity] = useState(item?.eventCity ?? "");
  const [eventType, setEventType] = useState<"" | "Online" | "In-Person">(
    () => {
      if (mode === "edit" || mode === "view") {
        const type = item?.eventType;
        if (type === "In-Person" || type === "Online") {
          return type;
        }
      }
      return "";
    },
  );

  const [ticketType, setTicketType] = useState<"" | "Free" | "Chargeable">(
    () => {
      if (mode === "edit" || mode === "view") {
        const type = item?.ticketType;

        if (type === "Free" || type === "Chargeable") {
          return type;
        }

        if (type === "FREE") {
          return "Free";
        }
      }

      return "";
    },
  );

  const [selectedFiles, setSelectedFiles] = useState<
    Array<{ id: string; file: File; preview: string }>
  >([]);

  React.useEffect(() => {
    return () => {
      selectedFiles.forEach((f) => URL.revokeObjectURL(f.preview));
    };
  }, [selectedFiles]);

  const [imageDetails, setImageDetails] = useState(item?.imageDetails ?? []);
  const [deletingImageIds, setDeletingImageIds] = useState<number[]>([]);

  const [deleteEventImage] = useDeleteEventImageMutation();

  React.useEffect(() => {
    setImageDetails(item?.imageDetails ?? []);
  }, [item?.imageDetails]);

  const plant = process.env.REACT_APP_PLANT;
  const shouldLoadRegistrations = !!item?.id && mode !== "add";

  const {
    data: allRegistrations,
    isLoading: isRegistrationsLoading,
    isError: isRegistrationsError,
  } = useGetAllEventRegistrationsQuery(
    { plant: plant as string },
    { skip: !shouldLoadRegistrations },
  );

  const registrationsForThisEvent =
    allRegistrations?.filter(
      (registration) => registration.eventHdrId === item?.id,
    ) ?? [];

  const isLoading = isCreating || isUpdating;

  function toggleAudience(val: AudienceType) {
    if (mode === "view") return;
    setAudience((prev) =>
      prev.includes(val) ? prev.filter((a) => a !== val) : [...prev, val],
    );
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (mode === "view") return;

    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);

    for (const rawFile of files) {
      // Validate file type
      if (!rawFile.type.startsWith("image/")) {
        continue;
      }

      // Downscale + re-encode on the client so the upload body stays small.
      const file = await compressImage(rawFile, { maxWidth: 1600 }).catch(
        () => rawFile,
      );

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        continue;
      }

      // Check current total limit
      const currentTotal = imageDetails.length + selectedFiles.length;
      if (currentTotal >= MAX_IMAGES) {
        break;
      }

      const preview = URL.createObjectURL(file);
      const newFileEntry = {
        id: `${file.name}-${file.size}-${Date.now()}`,
        file,
        preview,
        uploading: false
      };

      if (mode === "edit" && item?.id) {
        try {
          // Immediately upload if in edit mode (matching Glossary behavior)
          const uploadPayload = {
            plant,
            hdrId: item.id,
            files: [file]
          };
          
          const response = await uploadEventImage(uploadPayload).unwrap();

          // Assuming response contains the new image details or we should refetch
          // Most of our APIs return the uploaded image details in results
          if (response && Array.isArray(response.results) && response.results.length > 0) {
            setImageDetails(prev => [...prev, ...response.results]);
          }

          notifications.show({
            message: response.message,
            color: "teal",
          });
          URL.revokeObjectURL(preview);
        } catch (error: any) {
          notifications.show({
            message: error?.data?.message || "An error occurred",
            color: "red",
          });
          URL.revokeObjectURL(preview);
        }
      } else {
        // Add to the list for later upload (Add mode)
        setSelectedFiles(prev => [...prev, newFileEntry]);
      }
    }

    // Clear input
    e.target.value = "";
  };

  const handleDeleteImage = async (detId: number) => {
    if (mode === "view" || !item?.id) return;

    try {
      setDeletingImageIds((prev) => [...prev, detId]);
      const deleteRes = await deleteEventImage({ detId, eventId: item.id, plant }).unwrap();
      setImageDetails((prev) => prev.filter((d) => d.id !== detId));
      notifications.show({
        message: deleteRes.message,
        color: "teal",
      });
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    } finally {
      setDeletingImageIds((prev) => prev.filter((id) => id !== detId));
    }
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === "view") return;

    try {
      if (!startDate || !endDate) {
        return;
      }

      if (endDate <= startDate) {
        return;
      }

      // if (mode === "add" && (!files || files.length === 0)) {
      //   notifications.show({
      //     title: "Error",
      //     message: "Please upload at least one image file",
      //     color: "red",
      //     autoClose: 5000,
      //   });
      //   return;
      // }

      const eventDate = dayjs(startDate!).format("DD/MM/YYYY");
      const eventStartTime = dayjs(startDate!).format("HH:mm");
      const eventEndDate = dayjs(endDate!).format("DD/MM/YYYY");
      const eventEndTime = dayjs(endDate!).format("HH:mm");

      const eventData = {
        plant: plant,
        eventName: name,
        eventDescription: description,
        eventType: eventType,
        eventDate: eventDate,
        eventStartTime: eventStartTime,
        eventEndTime: eventEndTime,
        eventEndDate: eventEndDate,
        eventCity: eventCity,
        eventAddress: eventAddress,
        ticketType: ticketType === "Free" ? "FREE" : "Chargeable",
        audienceType: audience.length > 0 ? audience.join(",") : "Public",
      };

      const eventDataForUpdate =
        mode === "edit" && item?.id
          ? {
              ...eventData,
              id: item.id,
            }
          : eventData;

      const finalData = mode === "edit" ? eventDataForUpdate : eventData;

      const buildMultipartPayload = (dataObj: any) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(dataObj));

        if (selectedFiles.length > 0) {
          selectedFiles.forEach((entry) => {
            formData.append("file", entry.file);
          });
        } else {
          // send empty file so backend doesn't crash
          formData.append("file", new Blob([]));
        }

        return formData;
      };

      const payload = mode === "add" ? buildMultipartPayload(finalData) : finalData;

      if (mode === "edit" && item?.id) {
        const response: any = await updateEvent(payload).unwrap();

        // Note: Images are now uploaded immediately in Edit mode via handleFileChange
        // so we don't need to upload them here again.

        notifications.show({
          message: response?.message,
          color: "green",
        });
      } else {
        const response: any = await createEvent(payload as FormData).unwrap();
        notifications.show({
          message: response?.message,
          color: "green",
        });
      }

      onSave();
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
        autoClose: 5000,
      });
    }
  }

  const isViewMode = mode === "view";

  return (
    <div className="px-3">
      <>
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg border shadow-sm p-6 space-y-6"
        >
          <button
            id="eventsFormSubmit"
            type="submit"
            style={{ display: "none" }}
          />
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 mb-4">
            <div className="flex flex-col flex-1">
              <RequiredLabel text="Event Date & Start Time" />
              <DatePicker
                selected={
                  startDate instanceof Date && !isNaN(startDate.getTime())
                    ? startDate
                    : null
                }
                onChange={(d) => !isViewMode && setStartDate(d)}
                showTimeSelect
                timeFormat="hh:mm aa"
                dateFormat="dd/MM/yyyy hh:mm aa"
                placeholderText="Select date & start time"
                isClearable
                disabled={isViewMode}
                className="w-full"
              />
            </div>

            <div className="flex flex-col flex-1">
              <RequiredLabel text="Event End Date & Time" />{" "}
              <DatePicker
                selected={
                  endDate instanceof Date && !isNaN(endDate.getTime())
                    ? endDate
                    : null
                }
                onChange={(d) => !isViewMode && setEndDate(d)}
                showTimeSelect
                timeFormat="hh:mm aa"
                dateFormat="dd/MM/yyyy hh:mm aa"
                placeholderText="Select end date & time"
                isClearable
                disabled={isViewMode}
                className="w-full"
                minDate={startDate || undefined}
              />
            </div>
          </div>
          <div className="mb-3">
            <RequiredLabel text="Event Name" />
            <input
              style={fieldStyle}
              value={name}
              required
              onChange={(e) => !isViewMode && setName(e.target.value)}
              placeholder="Enter event name"
              disabled={isViewMode}
            />
          </div>
          <div className="mb-3">
            <RequiredLabel text="Event Description" />
            <textarea
              style={textareaStyle}
              value={description}
              maxLength={EVENT_DESCRIPTION_LIMIT}
              required
              onChange={(e) => !isViewMode && setDescription(e.target.value)}
              placeholder="Enter event description"
              disabled={isViewMode}
            />
            <div className="text-right text-xs text-gray-400 mt-1">
              {description.length}/{EVENT_DESCRIPTION_LIMIT}
            </div>
          </div>
          <div className="mb-3">
            <RequiredLabel text="Event Address" />
            <input
              style={fieldStyle}
              value={eventAddress}
              required
              onChange={(e) => !isViewMode && setEventAddress(e.target.value)}
              placeholder="Enter event address/venue"
              disabled={isViewMode}
            />
          </div>
          <div className="mb-3">
            <RequiredLabel text="Event City" />
            <input
              style={fieldStyle}
              value={eventCity}
              required
              onChange={(e) => !isViewMode && setEventCity(e.target.value)}
              placeholder="Enter city"
              disabled={isViewMode}
            />
          </div>
          <div className="mb-3">
            <label>Event Type</label>
            <select
              style={fieldStyle}
              value={eventType}
              required
              onChange={(e) =>
                !isViewMode &&
                setEventType(e.target.value as "" | "Online" | "In-Person")
              }
              disabled={isViewMode}
            >
              <option value="" disabled>
                Select event type
              </option>
              <option value="Online">Online</option>
              <option value="In-Person">In-Person</option>
            </select>
          </div>
          <div className="mb-3">
            <label>Ticket Type</label>
            <select
              style={fieldStyle}
              value={ticketType}
              required
              onChange={(e) =>
                !isViewMode &&
                setTicketType(e.target.value as "" | "Free" | "Chargeable")
              }
              disabled={isViewMode}
            >
              <option value="" disabled>
                Select ticket type
              </option>
              <option value="Free">Free</option>
              <option value="Chargeable">Chargeable</option>
            </select>
          </div>
          <div className="mb-3">
            <label>Audience Type</label>
            <div className="grid grid-cols-2 sm:flex sm:gap-6 gap-3 mt-2">
              {["Industry", "Government", "Academia", "Public"].map((type) => (
                <label key={type}>
                  <input
                    type="checkbox"
                    checked={audience.includes(type as AudienceType)}
                    onChange={() => toggleAudience(type as AudienceType)}
                    disabled={isViewMode}
                  />{" "}
                  {type}
                </label>
              ))}
            </div>
          </div>

          {(mode === "edit" || mode === "view") &&
            imageDetails?.length > 0 && (
              <div className="mb-3">
                <label>Current Images</label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {imageDetails.map((detail, index) => {
                    const isDeleting = deletingImageIds.includes(detail.id);
                    return (
                      <div key={detail.id} className="relative group">
                        <img
                          src={detail.url}
                          alt={`Event ${index + 1}`}
                          className="w-24 h-24 object-cover rounded border border-gray-300 hover:shadow-md transition-shadow"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "https://via.placeholder.com/96x96?text=Image+Error";
                          }}
                        />

                        {mode !== "view" && (
                          <button
                            type="button"
                          className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                            onClick={() => handleDeleteImage(detail.id)}
                            disabled={isDeleting}
                            title="Delete image"
                          >
                            {isDeleting ? (
                              <Loader size="xs" color="red" />
                            ) : (
                              <X size={14} />
                            )}
                          </button>
                        )}

                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center py-1">
                          Image {index + 1}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <small className="text-gray-500 mt-1 block">
                  {imageDetails.length} image(s) uploaded
                </small>
              </div>
            )}

          {!isViewMode && (
            <div className="mb-3">
              {imageDetails.length + selectedFiles.length < MAX_IMAGES && (
                <>
                  <label className="font-medium">
                    {mode === "add" ? "Upload Images" : "Upload New Image"}
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    style={fieldStyle}
                    accept="image/*"
                  />
                  <small className="text-gray-500 block mb-2">
                    Max {MAX_IMAGES} images total, 5MB each. Allowed: JPEG, PNG, GIF, WebP
                  </small>
                </>
              )}

              {selectedFiles.length > 0 && (
                <div className="mt-2 text-sm text-green-600">
                  Selected {selectedFiles.length} new file(s):
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedFiles.map((entry) => (
                      <div
                        key={entry.id}
                        className="relative group w-24 h-24 rounded border overflow-hidden"
                      >
                        <img
                          src={entry.preview}
                          alt={entry.file.name}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-100 transition-opacity"
                          onClick={() => {
                            setSelectedFiles((prev) =>
                              prev.filter((f) => f.id !== entry.id),
                            );
                            URL.revokeObjectURL(entry.preview);
                          }}
                          title="Remove selected image"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-1 text-xs text-gray-500">
                {imageDetails.length + selectedFiles.length} / {MAX_IMAGES} images
              </div>
            </div>
          )}
          <div className="sticky bottom-0 -mx-6 mt-6 border-t bg-white px-6 py-4 flex justify-end gap-3 shadow-[0_-2px_6px_rgba(0,0,0,0.05)]">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
              disabled={isLoading}
            >
              Cancel
            </button>

            {!isViewMode && (
              <button
                type="submit"
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading
                  ? "Saving..."
                  : mode === "edit"
                    ? "Update Event"
                    : "Create Event"}
              </button>
            )}
          </div>
        </form>
        {/* Read-only registrations table */}
        {item?.id && (
          <div style={{ ...cardStyle, marginTop: 16 }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold">Event registrations</h3>
              {isRegistrationsLoading && (
                <span className="text-xs text-gray-500">Loading…</span>
              )}
            </div>

            {isRegistrationsError ? (
              <p className="text-sm text-red-600">
                Failed to load registrations. Please try again later.
              </p>
            ) : registrationsForThisEvent.length === 0 ? (
              <p className="text-sm text-gray-500">
                No registrations have been submitted for this event yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left border-b">#</th>
                      <th className="px-3 py-2 text-left border-b">
                        Full Name
                      </th>
                      <th className="px-3 py-2 text-left border-b">Email</th>
                      <th className="px-3 py-2 text-left border-b">
                        Mobile Number
                      </th>
                      <th className="px-3 py-2 text-left border-b">City</th>
                      <th className="px-3 py-2 text-left border-b">State</th>
                      <th className="px-3 py-2 text-left border-b">Country</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrationsForThisEvent.map(
                      (reg: any, index: number) => {
                        const fullName = [reg.firstName, reg.lastName]
                          .filter(Boolean)
                          .join(" ");

                        return (
                          <tr
                            key={reg.id ?? index}
                            className="odd:bg-white even:bg-gray-50"
                          >
                            <td className="px-3 py-2 border-b align-top">
                              {index + 1}
                            </td>
                            <td className="px-3 py-2 border-b align-top">
                              {fullName || "-"}
                            </td>
                            <td className="px-3 py-2 border-b align-top">
                              {reg.email || "-"}
                            </td>
                            <td className="px-3 py-2 border-b align-top">
                              {reg.contactNumber || "-"}
                            </td>
                            <td className="px-3 py-2 border-b align-top">
                              {reg.city || "-"}
                            </td>
                            <td className="px-3 py-2 border-b align-top">
                              {reg.state || "-"}
                            </td>
                            <td className="px-3 py-2 border-b align-top">
                              {reg.country || "-"}
                            </td>
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* 
        </form>
       
      {item?.id && (
        <div style={{ ...cardStyle, marginTop: 16 }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold">Event registrations</h3>
            {isRegistrationsLoading && (
              <span className="text-xs text-gray-500">Loading…</span>
            )}
          </div>
        */}
      </>
    </div>
  );
};

export default EventsForm;
